import { createContext, useCallback, useContext, useState } from "react";
import { ConfigurablePage, PageConfigurationSchema } from "./pageConfiguration.types";

const STORAGE_KEY = "app:page-configuration";

// shape of all persisted page states
type PageConfigurationState = Partial<PageConfigurationSchema>;

// contract exposed by the context
type PageConfigurationContextType = {
	state: PageConfigurationState;

	// read config for a single page
	getPageConfiguration: <TPage extends ConfigurablePage>(page: TPage) => PageConfigurationSchema[TPage] | undefined;

	// update only part of a page config
	setPageConfiguration: <TPage extends ConfigurablePage>(
		page: TPage,
		partial: Partial<PageConfigurationSchema[TPage]>
	) => void;

	// reset everything
	clearAllPageConfigurations: VoidFunction;
};

// global context for page config persistence
const PageConfigurationContext = createContext<PageConfigurationContextType | null>(null);

// load initial state from localStorage
const readStorage = (): PageConfigurationState => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		return stored ? JSON.parse(stored) : {};
	} catch {
		// if corrupted, reset storage
		localStorage.removeItem(STORAGE_KEY);
		return {};
	}
};

// persist state to localStorage
const writeStorage = (state: PageConfigurationState) => {
	localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

export const PageConfigurationProvider = ({ children }: { children: React.ReactNode }) => {
	// global state for all page configurations
	const [state, setState] = useState<PageConfigurationState>(() => readStorage());

	// read configuration for a specific page
	const getPageConfiguration = useCallback(
		<TPage extends ConfigurablePage>(page: TPage) => state[page] as PageConfigurationSchema[TPage] | undefined,
		[state]
	);

	// update configuration for a specific page safely
	// merges old + new instead of overwriting everything
	const setPageConfiguration = useCallback(
		<TPage extends ConfigurablePage>(page: TPage, partial: Partial<PageConfigurationSchema[TPage]>) => {
			setState((prev) => {
				// current saved config for this page
				const current = prev[page] ?? ({} as PageConfigurationSchema[TPage]);

				// build new global state
				const updated: PageConfigurationState = {
					...prev,
					[page]: {
						...current,
						...partial,
					},
				};

				// persist immediately
				writeStorage(updated);

				return updated;
			});
		},
		[]
	);

	// reset everything stored in memory + storage
	const clearAllPageConfigurations = useCallback(() => {
		setState({});
		localStorage.removeItem(STORAGE_KEY);
	}, []);

	return (
		<PageConfigurationContext.Provider
			value={{
				state,
				getPageConfiguration,
				setPageConfiguration,
				clearAllPageConfigurations,
			}}
		>
			{children}
		</PageConfigurationContext.Provider>
	);
};

// hook to access global page configuration system
export const usePageConfiguration = () => {
	const ctx = useContext(PageConfigurationContext);

	if (!ctx) {
		throw new Error("usePageConfiguration must be used inside PageConfigurationProvider");
	}

	return ctx;
};
